import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import { SettingsPanel } from '@/shared/components/SettingsPanel';

import { CircleCheckIcon } from 'lucide-react';

import { FORGE_COPY, PREVIEW_COPY, SIGN_IN_COPY } from '../copy';
import {
  accountCardId,
  accountRows,
  forgeName,
  hostUsage,
  linkMethodOf,
  linkMethodWords,
  rowActions,
  showsExpiry,
  permissionsKnown,
  statusWords,
  type AccountRow,
  type HostUsage,
} from '../forgeAccounts';
import {
  isForgeSignInRefused,
  type useForgeAccounts,
} from '../hooks/useForgeAccounts';
import { outcomeForge, type LinkOutcome } from '../linkOutcome';
import { ForgeMark } from './ForgeMark';
import { LinkOutcomeMessage } from './LinkOutcomeMessage';
import { LinkTokenForm } from './LinkTokenForm';

type ForgeAccounts = ReturnType<typeof useForgeAccounts>;

/**
 * The person's own GitLab and GitHub accounts (spec 159 US1): one row per host
 * the organization connects to, linked or not, and the links for hosts no
 * longer used. One token form is open at a time.
 *
 * `id="linked-accounts"`: where a "relink your account" notification lands,
 * and where the forge's sign-in returns (spec 161), whose `outcome` is said at
 * the top.
 */
export function LinkedAccountsPanel({
  forge,
  outcome = null,
  onDismissOutcome,
  workspaceNames = new Map<string, string>(),
}: {
  forge: ForgeAccounts;
  outcome?: LinkOutcome | null;
  onDismissOutcome?: () => void;
  /** Workspace names by id, to say where each host is required (spec 163). */
  workspaceNames?: ReadonlyMap<string, string>;
}) {
  const [openHost, setOpenHost] = useState<string | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  // A "relink your account" notification opens /settings/commits#linked-accounts.
  // The router scrolls to a hash on its first render, before this page has
  // loaded and drawn the panel, so the panel brings itself into view.
  useEffect(() => {
    if (window.location.hash === '#linked-accounts') {
      anchor.current?.scrollIntoView?.({ block: 'start' });
    }
  }, []);
  const [unlinking, setUnlinking] = useState<AccountRow | null>(null);
  const { accounts, hosts, link, unlink, connect } = forge;
  const loading = accounts.isPending || hosts.isPending;
  const failed = accounts.isError || hosts.isError;
  const rows = accountRows(hosts.data?.items ?? [], accounts.data?.items ?? []);
  const hostViews = new Map(
    (hosts.data?.items ?? []).map((host) => [host.host, host]),
  );
  // The host a sign-in just linked: its card says so (spec 163 US4).
  const justLinked = outcome?.kind === 'linked' ? outcome.host : null;

  return (
    <div ref={anchor} id="linked-accounts" className="scroll-mt-6">
      <SettingsPanel labelledBy="commits-linked-accounts">
        <div className="flex flex-col gap-1">
          <h2
            id="commits-linked-accounts"
            className="text-foreground text-sm font-medium"
          >
            {FORGE_COPY.heading}
          </h2>
          <p className="text-muted-foreground text-sm">{FORGE_COPY.intro}</p>
        </div>

        {outcome ? (
          <LinkOutcomeMessage
            outcome={outcome}
            forge={outcomeForge(outcome, hosts.data?.items ?? [])}
            onDismiss={() => onDismissOutcome?.()}
          />
        ) : null}

        {loading ? (
          <p className="text-muted-foreground text-sm">{FORGE_COPY.loading}</p>
        ) : failed ? (
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground text-sm">
              {FORGE_COPY.loadFailed}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void accounts.refetch();
                void hosts.refetch();
              }}
            >
              {FORGE_COPY.retry}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{FORGE_COPY.empty}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((row) => (
              <AccountRowView
                key={row.host}
                row={row}
                usage={
                  hostViews.has(row.host)
                    ? hostUsage(hostViews.get(row.host)!, workspaceNames)
                    : null
                }
                justLinked={row.host === justLinked}
                open={openHost === row.host}
                onOpen={() => {
                  link.reset();
                  setOpenHost(row.host);
                }}
                onClose={() => setOpenHost(null)}
                onUnlink={() => setUnlinking(row)}
                link={link}
                connect={connect}
              />
            ))}
          </ul>
        )}
      </SettingsPanel>

      <ConfirmDestructiveDialog
        open={unlinking !== null}
        title={
          unlinking ? FORGE_COPY.unlinkTitle(forgeName(unlinking.provider)) : ''
        }
        confirmLabel={FORGE_COPY.unlink}
        isPending={unlink.isPending}
        onConfirm={() => {
          if (!unlinking) return;
          unlink.mutate(
            { host: unlinking.host, provider: unlinking.provider },
            { onSettled: () => setUnlinking(null) },
          );
        }}
        onCancel={() => setUnlinking(null)}
      >
        {FORGE_COPY.unlinkBody}
      </ConfirmDestructiveDialog>
    </div>
  );
}

function AccountRowView({
  row,
  usage,
  justLinked,
  open,
  onOpen,
  onClose,
  onUnlink,
  link,
  connect,
}: {
  row: AccountRow;
  /** Where the host is used and required; `null` for a host no longer used. */
  usage: HostUsage | null;
  justLinked: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onUnlink: () => void;
  link: ForgeAccounts['link'];
  connect: ForgeAccounts['connect'];
}) {
  const account = row.account;
  const working = account?.status === 'working';
  const actions = rowActions(row);
  const forgeLabel = forgeName(row.provider);
  const connecting = connect.isPending && connect.variables.host === row.host;
  const refused =
    isForgeSignInRefused(connect.error) && connect.error.host === row.host
      ? connect.error
      : null;
  const method = account ? linkMethodOf(account) : null;
  const permissions = account ? permissionsKnown(account) : null;

  const tokenButton =
    actions.main === 'link' ||
    actions.main === 'replace' ||
    actions.main === 'relink' ? (
      <Button size="sm" variant="outline" onClick={onOpen}>
        {actions.main === 'link'
          ? FORGE_COPY.link
          : actions.main === 'replace'
            ? FORGE_COPY.replace
            : FORGE_COPY.relink}
      </Button>
    ) : null;
  const connectButton =
    actions.main === 'connect' ? (
      <Button
        size="sm"
        variant={account ? 'outline' : 'default'}
        disabled={connect.isPending}
        onClick={() =>
          connect.mutate({ host: row.host, provider: row.provider })
        }
      >
        <ForgeMark provider={row.provider} className="size-4" />
        {connecting
          ? SIGN_IN_COPY.connecting(forgeLabel)
          : SIGN_IN_COPY.connect(forgeLabel)}
      </Button>
    ) : null;
  const tokenInstead = actions.tokenInstead ? (
    <Button size="sm" variant="link" className="h-auto px-0" onClick={onOpen}>
      {SIGN_IN_COPY.tokenInstead}
    </Button>
  ) : null;
  const unlinkButton = actions.unlink ? (
    <Button size="sm" variant="ghost" onClick={onUnlink}>
      {FORGE_COPY.unlink}
    </Button>
  ) : null;

  return (
    <li
      id={accountCardId(row.host)}
      className={`bg-card flex flex-col gap-3 rounded-lg border p-4 transition-[border-color,box-shadow] duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] ${
        justLinked
          ? 'border-state-success ring-state-success/20 ring-4'
          : 'border-border'
      }`}
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <span className="bg-muted text-foreground grid size-9 shrink-0 place-items-center rounded-lg">
          <ForgeMark provider={row.provider} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-medium [overflow-wrap:anywhere]">
              {row.host}
            </span>
            <span className="text-muted-foreground">{forgeLabel}</span>
            {account && method ? (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  working
                    ? 'bg-state-success/10 text-state-success'
                    : 'bg-state-danger/10 text-state-danger'
                }`}
              >
                {justLinked ? (
                  <CircleCheckIcon
                    aria-hidden="true"
                    className="animate-in zoom-in-50 fade-in-0 size-3.5 duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] motion-reduce:animate-none"
                  />
                ) : null}
                {working ? linkMethodWords(method) : statusWords(account)}
              </span>
            ) : (
              <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
                {FORGE_COPY.notLinked}
              </span>
            )}
          </p>
          <p className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            {account ? (
              <span className="text-foreground">@{account.forge_username}</span>
            ) : null}
            {account && working && method === 'oauth' ? (
              <span>{PREVIEW_COPY.renews}</span>
            ) : null}
            {account && showsExpiry(account) && account.expires_at ? (
              <span>{FORGE_COPY.expires(account.expires_at.slice(0, 10))}</span>
            ) : null}
            {permissions !== null ? (
              <span>
                {permissions
                  ? PREVIEW_COPY.permissionsOk
                  : PREVIEW_COPY.permissionsUnknown}
              </span>
            ) : null}
            {usage ? (
              <span>
                {PREVIEW_COPY.usedIn(usage.count)}
                {' · '}
                {usage.required.length > 0 ? (
                  <span className="text-state-info font-medium">
                    {PREVIEW_COPY.requiredIn(usage.required.join(', '))}
                  </span>
                ) : usage.requiredUnnamed > 0 ? (
                  <span className="text-state-info font-medium">
                    {PREVIEW_COPY.requiredInCount(usage.requiredUnnamed)}
                  </span>
                ) : (
                  PREVIEW_COPY.notRequired
                )}
              </span>
            ) : row.orphan ? (
              <span>{FORGE_COPY.notUsed}</span>
            ) : null}
          </p>
        </div>
        {/* On a phone the actions take a full-width row under the details:
            beside them, Connect left the host name a 65px column, a letter
            group per line (spec 275). */}
        {!open ? (
          <span className="flex flex-wrap items-center gap-2 max-md:w-full max-md:flex-nowrap max-md:[&>*]:flex-1">
            {connectButton}
            {tokenButton}
            {unlinkButton}
          </span>
        ) : null}
      </div>

      {!open && tokenInstead ? (
        <div className="flex justify-end max-md:justify-center">
          {tokenInstead}
        </div>
      ) : null}

      {refused ? (
        <p role="alert" className="text-state-danger text-xs">
          {refused.message}
        </p>
      ) : null}

      {open ? (
        <LinkTokenForm
          provider={row.provider}
          host={row.host}
          link={link}
          onDone={onClose}
        />
      ) : null}
    </li>
  );
}
